import { useCallback, useEffect, useRef, useState } from 'react';
import { ChatPanel, DEFAULT_FIRST_MESSAGE } from '../components/ChatPanel';
import { ControlsPanel } from '../components/ControlsPanel';
import { EyePreview } from '../components/EyePreview';
import { LineageStrip } from '../components/LineageStrip';
import { DominantSwatch } from '../components/DominantSwatch';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider';
import { AnnotationLayer } from '../components/AnnotationLayer';
import { VariationsGrid } from '../components/VariationsGrid';
import { useToast } from '../components/Toast';
import { useChat } from '../hooks/useChat';
import { resolveAssetUrl, type CatalogueItem } from '../api/client';
import type { ChatResponseItem } from '../api/client';
import { api, type UploadResponse } from '../api/client';
import { hexToColourName } from '../lib/colour-name';
import type { Parameters } from '../types/parameters';
import { EMOTION_OPTIONS } from '../types/parameters';
import { useLanguage } from '../lib/language';
import type { Annotation } from '../types/annotations';

export interface StudioRouteProps {
  /**
   * Optional polite-status announcer passed down from <App>. Used to push
   * screen-reader-only messages when the variations grid arrives so the
   * user doesn't have to spot the new content visually.
   */
  announce?: (message: string) => void;
}

export function StudioRoute(props: StudioRouteProps = {}) {
  const { announce } = props;
  const chat = useChat();
  const toast = useToast();
  const { lang } = useLanguage();

  // v7 — feature state lives here so the Studio toolbar (EyePreview) and
  // the ColorPicker can cooperate. None of these state values round-trip
  // through the backend; they're purely local UI affordances.
  const [eyedropperArmed, setEyedropperArmed] = useState(false);
  const [eyedropperImage, setEyedropperImage] = useState<HTMLImageElement | null>(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [annotateArmed, setAnnotateArmed] = useState(false);
  // v7.2 — annotations live inside useChat so hydrate / persist /
  // clearAll share the same state. Read + write through the hook.
  const annotations = chat.annotations;
  const setAnnotations = chat.setAnnotations;
  // v7 — uploaded reference image. When set, the next chat-with-reference
  // call includes it; after the call resolves we clear it.
  const [reference, setReference] = useState<UploadResponse | null>(null);

  const [saveTitle, setSaveTitle] = useState('');
  const [saveNotes, setSaveNotes] = useState('');
  const [saveTags, setSaveTags] = useState('');
  const [saveMood, setSaveMood] = useState<string>('');
  const [saveColourFamily, setSaveColourFamily] = useState<string>('');
  const [saveProject, setSaveProject] = useState<string>('');
  // v5 a11y — chat-first default. The right column shows a one-line intro
  // and a "Fine-tune by hand" disclosure until mum opts into manual
  // controls. Once opened, a "Hide" button in the panel collapses it.
  const [manualOpen, setManualOpen] = useState(false);
  const autoFiredRef = useRef(false);

  // v5 — auto-derive Mood / Colour-family from the latest parameters so
  // mum doesn't have to fill every field manually. They can still
  // override before saving.
  useEffect(() => {
    const p = chat.currentParameters;
    if (p.emotion && p.emotion !== 'neutral' && !saveMood) {
      setSaveMood(p.emotion);
    }
    if (p.iris_color && !saveColourFamily) {
      try {
        const family = hexToColourName(p.iris_color);
        // ColourFamily has label + swatch — use label as the canonical
        // string sent to the catalogue API. The catalogue column is
        // free-text so any string is acceptable.
        setSaveColourFamily(family.label);
      } catch {
        /* ignore — colour parser failed */
      }
    }
    // We deliberately do NOT depend on chat.currentParameters every change
    // — the auto-derive should only fire once per "epoch" so editing in
    // the controls doesn't yank saved values back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.currentImageId]);

  // Auto-fire one default chat request on first mount so the Studio never
  // opens to a blank canvas. Subsequent visits keep the last image visible
  // until the user types something or clicks Apply.
  //
  // v6-polish — small delay (~1.5s) before firing so the suggested
  // prompt chips below the empty chat have time to land on screen and
  // be read. Without the delay the auto-fire's "user turn" immediately
  // flips `history.length` to 1 and hides the chips before they're
  // useful. A 1.5s pause matches how long it takes a user to read 3–4
  // short prompts; if mum clicks one before then the timeout cancels
  // the auto-fire (handled by `cancelledRef`).
  useEffect(() => {
    if (autoFiredRef.current) return;
    if (chat.currentImageUrl) return;
    autoFiredRef.current = true;
    const t = window.setTimeout(() => {
      void chat.send(DEFAULT_FIRST_MESSAGE);
    }, 1500);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.currentImageUrl]);

  /**
   * v7.2 — wipe the persisted session (eye + chat + annotations) and
   * reset every piece of local Studio state so the route behaves as if
   * the user opened a fresh tab. Wired to the "Start fresh" button.
   */
  const handleClearAll = useCallback(() => {
    chat.onClearAll();
    setReference(null);
    setEyedropperArmed(false);
    setCompareOpen(false);
    setAnnotateArmed(false);
    setSaveTitle('');
    setSaveNotes('');
    setSaveTags('');
    setSaveMood('');
    setSaveColourFamily('');
    setSaveProject('');
    toast.push('info', 'Started a fresh session.');
  }, [chat, toast]);

  /**
   * "Generate" — current Parameters, same seed + subject_reference.
   * Mum is iterating on the current reference; the new image will be
   * deterministic and only the prompted changes vary.
   */
  const handleGenerate = useCallback(() => {
    const params: Parameters = { ...chat.currentParameters, n_variations: 1 };
    void chat.applyParameters(params, 'Generate from current settings');
  }, [chat]);

  /**
   * "Generate fresh" — current Parameters, NEW seed, no subject_reference.
   * Breaks the iteration chain on purpose.
   */
  const handleGenerateFresh = useCallback(() => {
    const params: Parameters = { ...chat.currentParameters, n_variations: 1 };
    void chat.generateFresh(params);
  }, [chat]);

  /**
   * "Generate 4 variations" — current Parameters with n_variations=4.
   * Backend returns a `ChatVariationResponse` (4 items); useChat stores
   * them and `pickingVariation` becomes true. The grid below picks one.
   *
   * v7.1 — pass `expectVariations: true` so the in-flight sendInternal
   * doesn't wipe the existing variations grid on the way out (a rapid
   * double-click on this button no longer empties the grid while the new
   * request is still resolving).
   */
  const handleGenerateVariations = useCallback(() => {
    const params: Parameters = { ...chat.currentParameters, n_variations: 4 };
    void chat.applyParameters(params, 'Generate 4 variations from current settings', { expectVariations: true });
  }, [chat]);

  const handleApply = useCallback(() => {
    void chat.applyParameters(chat.currentParameters);
  }, [chat]);

  // v7 — reference image upload handler. The user-uploaded file is POSTed
  // to /api/chat-with-reference/upload; the response contains the URL the
  // backend will serve it at, which we pass to /api/chat-with-reference on
  // the next chat send.
  const handleUploadReference = useCallback(
    async (file: File) => {
      try {
        toast.push('info', 'Uploading reference…');
        const res = await api.uploadReference(file);
        setReference(res);
        toast.push('success', 'Reference attached.');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Reference upload failed.';
        toast.push('error', message);
      }
    },
    [toast],
  );

  /**
   * v7 — chat-with-reference aware send. Routes through
   * /api/chat-with-reference so the reference URL is passed to MiniMax
   * as `input_urls`. Bypasses the hook's `send` and feeds the response
   * back through setAsStartingPoint (handles both single + variations).
   */
  const handleSendWithReference = useCallback(
    async (msg: string) => {
      if (!reference) {
        void chat.send(msg);
        return;
      }
      try {
        const res = await api.chatWithReference({
          user_message: msg,
          current_parameters: chat.currentParameters,
          current_image_url: chat.currentImageUrl,
          current_seed: chat.currentSeed,
          chat_history: chat.chatHistory.map((t) => ({ role: t.role, content: t.content })),
          reference_url: reference.image_url,
        });
        if ('items' in res && Array.isArray(res.items) && res.items[0]) {
          chat.pickVariation(res.items[0] as ChatResponseItem);
        } else if ('image_url' in res) {
          const u = res as {
            id: string; image_url: string; thumbnail_url: string;
            seed: number; parameters: Parameters; explanation: string;
            parent_id: string | null;
          };
          chat.setAsStartingPoint({
            imageId: u.id, imageUrl: u.image_url, thumbnailUrl: u.thumbnail_url,
            parameters: u.parameters, seed: u.seed, parentId: u.parent_id,
            note: u.explanation,
          });
        }
        setReference(null);
        toast.push('success', 'Reference applied.');
      } catch (err) {
        toast.push('error', err instanceof Error ? err.message : 'Chat-with-reference failed.');
      }
    },
    [reference, chat, toast],
  );

  const handleSave = useCallback(async () => {
    if (!chat.currentImageId) {
      toast.push('info', 'Generate a reference first, then save it.');
      return;
    }
    const tags = saveTags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    const saved = await chat.saveToCatalogue({
      title: saveTitle.trim() || 'Untitled reference',
      notes: saveNotes.trim(),
      tags,
      mood: saveMood.trim() || null,
      colour_family: saveColourFamily.trim() || null,
      project_tag: saveProject.trim() || null,
      annotations,
    });
    if (saved) {
      toast.push('success', 'Saved to your catalogue.');
      setSaveTitle('');
      setSaveNotes('');
      setSaveTags('');
      setSaveMood('');
      setSaveColourFamily('');
      setSaveProject('');
      const optimistic: CatalogueItem = {
        id: saved.id,
        generated_id: saved.generated_id,
        title: saved.title,
        notes: saved.notes,
        tags: saved.tags,
        created_at: saved.created_at,
        image_url: saved.image_url,
        thumbnail_url: saved.thumbnail_url,
        annotations,
      };
      window.dispatchEvent(
        new CustomEvent('eye-gallery:catalogue-added', { detail: optimistic }),
      );
    } else if (chat.saveErrorMessage) {
      toast.push('error', chat.saveErrorMessage);
    }
  }, [
    chat,
    chat.saveErrorMessage,
    saveNotes,
    saveTags,
    saveTitle,
    saveMood,
    saveColourFamily,
    saveProject,
    toast,
  ]);

  const generating = chat.sending;
  const hasImage = Boolean(chat.currentImageUrl);

  // v7 — clear the eyedropper / compare / annotate armed state when the
  // canonical image changes so stale flags don't bleed into the next eye.
  // v7.2 — annotation hydration now lives in useChat (lastLoadedEyeIdRef
  // effect), so we don't reset annotations here.
  useEffect(() => {
    setEyedropperArmed(false);
    setCompareOpen(false);
    setAnnotateArmed(false);
  }, [chat.currentImageId]);

  // v5 — handle picking a lineage ancestor (parent / grandparent / …).
  // Reuse setAsStartingPoint which copies parameters + seed into the
  // current Studio state without firing a chat round-trip.
  // v7 — also seed the annotations state with the picked eye's stored
  // hotspots so the user can read them in read-only mode.
  const handleLineagePick = useCallback(
    (entry: { id: string; image_url: string; thumbnail_url: string; parameters: Parameters; seed: number; annotations?: Annotation[] }) => {
      chat.setAsStartingPoint({
        imageId: entry.id,
        imageUrl: entry.image_url,
        thumbnailUrl: entry.thumbnail_url,
        parameters: entry.parameters,
        seed: entry.seed,
        parentId: null,
        note: 'Stepped back through the lineage.',
      });
      if (entry.annotations && entry.annotations.length > 0) {
        setAnnotations(entry.annotations);
      }
      toast.push('success', 'Stepped back to a previous reference.');
    },
    [chat, toast],
  );

  return (
    <section
      id="panel-studio"
      role="tabpanel"
      aria-labelledby="tab-studio"
      className="flex flex-col gap-6"
    >
      {/* v7.2 — Start fresh button. Wipes the persisted session (eye +
          chat + annotations) and resets every piece of local Studio
          state. Pinned at the top so it's always reachable. */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleClearAll}
          aria-label="Clear and start over"
          className="inline-flex h-9 cursor-pointer items-center rounded-md border border-neutral-300 px-3 text-xs font-medium text-neutral-800 hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700 dark:text-neutral-100 dark:hover:bg-neutral-800"
        >
          {lang === 'plain' ? 'Clear and start over' : 'Clear session'}
        </button>
      </div>

      <div className="flex flex-col gap-2">
        {chat.pickingVariation && chat.variations ? (
          <VariationsGrid
            items={chat.variations}
            onPick={(item) => {
              chat.pickVariation(item);
              toast.push('success', 'Picked one as your new reference.');
            }}
            onDismiss={() => chat.dismissVariations()}
            announce={announce}
          />
        ) : (
          <EyePreview
            imageUrl={chat.currentImageUrl}
            generating={generating}
            parameters={chat.currentParameters}
            imageId={chat.currentImageId}
            onSetAsStarting={undefined}
            eyedropperArmed={eyedropperArmed}
            onEyedropperToggle={() => setEyedropperArmed((v) => !v)}
            compareOpen={compareOpen}
            onCompareToggle={() => setCompareOpen((v) => !v)}
            annotateArmed={annotateArmed}
            onAnnotateToggle={() => setAnnotateArmed((v) => !v)}
            hasParent={Boolean(chat.parentId)}
            imageElementRef={setEyedropperImage}
          />
        )}

        {/* v7 — Compare-with-previous overlay. Opened from the EyePreview
            toolbar. */}
        {compareOpen && chat.parentId && chat.currentImageUrl ? (
          <CompareWithParentOverlay
            parentId={chat.parentId}
            currentImageUrl={chat.currentImageUrl}
            onClose={() => setCompareOpen(false)}
          />
        ) : null}

        {/* v7 — Annotation layer. Renders over the eye preview so hotspots
            land on the iris regardless of crop / resize. */}
        {!chat.pickingVariation && chat.currentImageUrl ? (
          <AnnotationLayerHost
            armed={annotateArmed}
            imageId={chat.currentImageId}
            annotations={annotations}
            onChange={setAnnotations}
          />
        ) : null}

        {/* v5 — Lineage strip. Visible once the current image has at least
            one ancestor. Mum can step back to any previous generation. */}
        {chat.parentId && chat.currentImageId ? (
          <LineageStrip
            currentImageId={chat.currentImageId}
            currentThumbnailUrl={chat.currentThumbnailUrl}
            onPick={handleLineagePick}
          />
        ) : null}

        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          {chat.pickingVariation
            ? 'Four variations came back — click one to make it your new reference.'
            : hasImage
              ? chat.currentSeed !== null
                ? `Most recent generation. Seed ${chat.currentSeed} — each new message creates a deterministic update based on this one.`
                : 'Most recent generation. Each new message creates a new image based on this one.'
              : 'Generating your first reference image automatically. It will appear here in a few seconds.'}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
        {/* v7.2 — Restored-session banner. Visible only right after a
            hydrate from localStorage; auto-hides as soon as the user
            sends a new chat message (handled inside useChat's
            sendInternal). The × button dismisses without sending. */}
        {chat.showRestoredNotice ? (
          <div
            role="status"
            data-testid="restored-notice"
            className="col-span-full flex items-start gap-3 rounded-xl border border-accent/40 bg-accent/10 p-3 text-sm"
          >
            <p className="flex-1">
              Restored from your last session — your last eye is still here.
              Click <span className="font-semibold">Start fresh</span> to clear.
            </p>
            <button
              type="button"
              onClick={() => chat.dismissRestoredNotice()}
              aria-label="Dismiss restored session notice"
              className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-base leading-none text-neutral-700 hover:bg-white/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:text-neutral-200 dark:hover:bg-neutral-800/60"
            >
              ×
            </button>
          </div>
        ) : null}
        <ChatPanel
          history={chat.chatHistory}
          sending={chat.sending}
          errorMessage={chat.errorMessage}
          onSend={(msg) => void handleSendWithReference(msg)}
          onCancel={() => chat.cancel()}
          onUploadReference={(file) => void handleUploadReference(file)}
          referencePreviewUrl={reference ? resolveAssetUrl(reference.image_url) ?? reference.image_url : null}
          onClearReference={() => setReference(null)}
          headerTrailing={
            !manualOpen ? (
              <button
                type="button"
                onClick={() => setManualOpen(true)}
                aria-label="Open manual reflection settings"
                className="inline-flex h-11 cursor-pointer items-center justify-center rounded-md border border-neutral-300 px-3 text-xs font-medium text-neutral-700 hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
              >
                Fine-tune by hand
              </button>
            ) : null
          }
        />

        <div className="flex flex-col gap-4">
          {manualOpen ? (
            <ControlsPanel
              parameters={chat.currentParameters}
              onChange={chat.updateParameter}
              sending={chat.sending}
              onApply={handleApply}
              onGenerate={handleGenerate}
              onGenerateFresh={handleGenerateFresh}
              onGenerateVariations={handleGenerateVariations}
              pickingVariation={chat.pickingVariation}
              defaultOpen={false}
              onHidePanel={() => setManualOpen(false)}
              setParameters={chat.setParameters}
              eyedropperArmed={eyedropperArmed}
              eyedropperImage={eyedropperImage}
              onEyedropSample={(hex) => {
                toast.push('success', `Picked colour ${hex}`);
              }}
              onEyedropCancel={() => setEyedropperArmed(false)}
            />
          ) : (
            <ManualDisclosureIntro onOpen={() => setManualOpen(true)} />
          )}

          {hasImage ? (
            <SaveReferenceCard
              chat={chat}
              saveTitle={saveTitle}
              saveNotes={saveNotes}
              saveTags={saveTags}
              saveMood={saveMood}
              saveColourFamily={saveColourFamily}
              saveProject={saveProject}
              onTitleChange={setSaveTitle}
              onNotesChange={setSaveNotes}
              onTagsChange={setSaveTags}
              onMoodChange={setSaveMood}
              onColourFamilyChange={setSaveColourFamily}
              onProjectChange={setSaveProject}
              onSave={() => void handleSave()}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
}

/**
 * One-line intro + disclosure button shown in the right column when the
 * chat-first mode is active. Replaces the always-visible ControlsPanel
 * (AAA default-collapse).
 */
function ManualDisclosureIntro(props: { onOpen: () => void }) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-4 text-sm text-neutral-700 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200">
      <p>
        Want to tweak the eye by hand instead of asking the AI? Open the
        manual settings to adjust iris, catchlight, reflected scene, and
        more.
      </p>
      <button
        type="button"
        onClick={props.onOpen}
        className="inline-flex h-11 cursor-pointer items-center justify-center self-start rounded-md border border-neutral-300 px-4 text-sm font-semibold text-neutral-800 hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700 dark:text-neutral-100 dark:hover:bg-neutral-800"
      >
        Fine-tune by hand
      </button>
    </div>
  );
}

interface SaveReferenceCardProps {
  chat: ReturnType<typeof useChat>;
  saveTitle: string;
  saveNotes: string;
  saveTags: string;
  saveMood: string;
  saveColourFamily: string;
  saveProject: string;
  onTitleChange: (next: string) => void;
  onNotesChange: (next: string) => void;
  onTagsChange: (next: string) => void;
  onMoodChange: (next: string) => void;
  onColourFamilyChange: (next: string) => void;
  onProjectChange: (next: string) => void;
  onSave: () => void;
}

function SaveReferenceCard(props: SaveReferenceCardProps) {
  const {
    chat,
    saveTitle,
    saveNotes,
    saveTags,
    saveMood,
    saveColourFamily,
    saveProject,
    onTitleChange,
    onNotesChange,
    onTagsChange,
    onMoodChange,
    onColourFamilyChange,
    onProjectChange,
    onSave,
  } = props;

  const { lang } = useLanguage();
  const isPlain = lang === 'plain';
  const emotionOptions = EMOTION_OPTIONS;
  // Six manual override colour families. The auto-derived value comes
  // from `hexToColourName`; these six are mum's manual overrides if she
  // wants a different label than the parser suggests.
  const colourFamilyChoices: { id: string; label: string; hex: string }[] = [
    { id: 'red', label: 'Red', hex: '#b03a2e' },
    { id: 'amber', label: 'Amber', hex: '#e09f3e' },
    { id: 'green', label: 'Green', hex: '#3a7d44' },
    { id: 'blue', label: 'Blue', hex: '#3a6ea5' },
    { id: 'violet', label: 'Violet', hex: '#7b2cbf' },
    { id: 'silver', label: 'Silver', hex: '#9aa0a6' },
  ];

  const moodChipClass = (active: boolean) =>
    [
      'inline-flex h-9 cursor-pointer items-center justify-center rounded-full border px-3 text-xs font-medium motion-safe:transition-colors',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
      active
        ? 'border-accent bg-accent/20 text-accent dark:bg-accent/25'
        : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800',
    ].join(' ');

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">
          Save this reference
        </h2>
        <span className="text-xs text-neutral-500 dark:text-neutral-400">
          id {chat.currentImageId?.slice(0, 8)}
        </span>
      </div>

      {/* v5 — dominant-colour swatch beside the current preview so mum
          can confirm what the auto-derived colour-family will be.
          v7 — also surface the plain-language description below the swatch
          so mum (aphantasia) can read what's about to be saved. Plain mode
          only. */}
      {chat.currentImageUrl ? (
        <div className="flex items-start gap-3 rounded-md border border-neutral-200 bg-neutral-50 p-2 dark:border-neutral-800 dark:bg-neutral-950">
          <img
            src={resolveAssetUrl(chat.currentImageUrl)}
            alt="Current reference preview"
            className="h-16 w-16 rounded-md object-cover"
          />
          <div className="flex flex-1 flex-col gap-1">
            <DominantSwatch imageUrl={chat.currentImageUrl} />
            {chat.currentDescription && isPlain ? (
              <span
                data-testid="save-card-description"
                className="text-[11px] leading-snug text-neutral-600 dark:text-neutral-300"
              >
                {chat.currentDescription}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      <label className="flex flex-col gap-1">
        <span className="text-xs text-neutral-600 dark:text-neutral-400">
          Title
        </span>
        <input
          type="text"
          value={saveTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="e.g. Stormy sky reflection"
          className="h-11 cursor-text rounded-md border border-neutral-300 bg-white px-3 text-sm text-neutral-900 focus:border-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-xs text-neutral-600 dark:text-neutral-400">
          Notes
        </span>
        <textarea
          value={saveNotes}
          onChange={(e) => onNotesChange(e.target.value)}
          rows={2}
          placeholder="Anything to remember later"
          className="rounded-md border border-neutral-300 bg-white p-3 text-sm text-neutral-900 focus:border-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
        />
      </label>

      {/* v5 — Mood chip selector. Single-select from EMOTION_OPTIONS,
          auto-derived from parameters.emotion when the image lands. */}
      <fieldset className="flex flex-col gap-2">
        <legend className="text-xs text-neutral-600 dark:text-neutral-400">
          Mood
        </legend>
        <div className="flex flex-wrap gap-1.5">
          {emotionOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              aria-pressed={saveMood === opt.value}
              onClick={() => onMoodChange(saveMood === opt.value ? '' : opt.value)}
              className={moodChipClass(saveMood === opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </fieldset>

      {/* v5 — Colour-family chips. Six manual override choices plus the
          auto-derived value highlighted by default. */}
      <fieldset className="flex flex-col gap-2">
        <legend className="text-xs text-neutral-600 dark:text-neutral-400">
          Colour family
        </legend>
        <div className="flex flex-wrap gap-1.5">
          {colourFamilyChoices.map((c) => (
            <button
              key={c.id}
              type="button"
              aria-pressed={saveColourFamily === c.id}
              onClick={() =>
                onColourFamilyChange(saveColourFamily === c.id ? '' : c.id)
              }
              className={[
                'inline-flex h-9 cursor-pointer items-center gap-2 rounded-full border px-2 text-xs font-medium motion-safe:transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                saveColourFamily === c.id
                  ? 'border-accent bg-accent/20 text-accent dark:bg-accent/25'
                  : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800',
              ].join(' ')}
            >
              <span
                aria-hidden="true"
                className="inline-block h-3 w-3 rounded-full border border-black/10 dark:border-white/20"
                style={{ backgroundColor: c.hex }}
              />
              {c.label}
            </button>
          ))}
        </div>
      </fieldset>

      <label className="flex flex-col gap-1">
        <span className="text-xs text-neutral-600 dark:text-neutral-400">
          Project
        </span>
        <input
          type="text"
          value={saveProject}
          onChange={(e) => onProjectChange(e.target.value)}
          placeholder="e.g. Gothic portrait series"
          className="h-11 cursor-text rounded-md border border-neutral-300 bg-white px-3 text-sm text-neutral-900 focus:border-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-xs text-neutral-600 dark:text-neutral-400">
          Tags (comma-separated)
        </span>
        <input
          type="text"
          value={saveTags}
          onChange={(e) => onTagsChange(e.target.value)}
          placeholder="stormy, blue, dramatic"
          className="h-11 cursor-text rounded-md border border-neutral-300 bg-white px-3 text-sm text-neutral-900 focus:border-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
        />
      </label>

      <div className="flex items-center justify-between gap-2">
        {chat.currentImageUrl ? (
          <a
            href={resolveAssetUrl(chat.currentImageUrl)}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-accent-light underline-offset-2 hover:underline focus:outline-none focus-visible:underline dark:text-[#a8c0ff]"
          >
            Open full-size
          </a>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={onSave}
          disabled={chat.savingToCatalogue}
          aria-busy={chat.savingToCatalogue}
          className="inline-flex h-11 cursor-pointer items-center justify-center rounded-md bg-accent px-5 text-sm font-semibold text-neutral-950 motion-safe:transition-colors hover:bg-accent-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-60 dark:focus-visible:ring-offset-neutral-950"
        >
          {chat.savingToCatalogue ? 'Saving…' : 'Save to catalogue'}
        </button>
      </div>
    </div>
  );
}

/**
 * 2×2 grid of the 4 variations returned by `n_variations=4`.
 * Each tile shows the thumbnail, its seed, and a "Pick" button (plain:
 * "Use this one" / technical: "Select"). Dismissing the grid keeps the
 * previous canonical image intact.
 *
 * v5 a11y:
 *  - When this grid mounts (variations just arrived), focus snaps to the
 *    "Keep previous" button — the safest default that never destroys
 *    work. Screen readers also hear a polite "Four variations are
 *    ready — pick one" status update.
 *  - Each variation image gets a descriptive alt text derived from the
 *    underlying Parameters (catchlight / lid / iris pattern).
 *
 * v7 — keyboard navigation: arrow keys move focus between tiles; Enter
 * picks the focused tile. The "selected" tile (when one is hovered or
 * keyboard-focused) gets a 2-pixel accent ring; clicking or pressing
 * Enter picks it as the new current image.
 *
 * v7.3 — VariationsGrid extracted to components/VariationsGrid.tsx with
 * click-to-blow-up, tap-and-compare, hover metadata, and polished
 * keyboard navigation.
 */

/* v7 — before/after overlay wrapper. Lazy-loads the parent image URL via
 * /api/catalogue/{id}/load and opens the BeforeAfterSlider. Falls back to
 * /api/images/{id}.png if the catalogue row is missing. */
function CompareWithParentOverlay(props: { parentId: string; currentImageUrl: string; onClose: () => void }) {
  const { parentId, currentImageUrl, onClose } = props;
  const [parentUrl, setParentUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        let url: string | null = null;
        try {
          const r = await fetch(`/api/catalogue/${parentId}/load`);
          if (r.ok) {
            const data = (await r.json()) as { image_url?: string };
            if (data.image_url) url = data.image_url;
          }
        } catch { /* fall through to the static fallback */ }
        if (!url) url = `/api/images/${parentId}.png`;
        if (!cancelled) setParentUrl(url);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load parent');
      }
    })();
    return () => { cancelled = true; };
  }, [parentId]);

  if (error) {
    return (
      <div role="alert" className="fixed inset-0 z-40 flex items-center justify-center bg-neutral-950/80 p-4 backdrop-blur" onClick={onClose}>
        <div className="rounded-lg border border-rose-300 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-700 dark:bg-rose-900/30 dark:text-rose-100">{error}</div>
      </div>
    );
  }
  if (!parentUrl) return null;
  return (
    <BeforeAfterSlider
      beforeUrl={resolveAssetUrl(parentUrl) ?? parentUrl}
      afterUrl={resolveAssetUrl(currentImageUrl) ?? currentImageUrl}
      alt="Eye comparison"
      onClose={onClose}
    />
  );
}

/* v7 — annotation layer host. Positions the AnnotationLayer on top of
 * the EyePreview's aspect-ratio container so hotspots land on the iris
 * regardless of crop / resize. */
function AnnotationLayerHost(props: {
  armed: boolean;
  imageId: string | null;
  annotations: Annotation[];
  onChange: (next: Annotation[]) => void;
}) {
  const { armed, imageId, annotations, onChange } = props;
  const [autoOpenLastId, setAutoOpenLastId] = useState<string | null>(null);
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!imageId) return;
    const root = document.querySelector('[data-testid="eye-preview-root"]');
    if (!root) return;
    const inner = root.querySelector('.aspect-\\[16\\/10\\]') as HTMLDivElement | null;
    if (inner) setContainerEl(inner);
  }, [imageId]);

  if (!containerEl) return null;
  const rect = containerEl.getBoundingClientRect();
  return (
    <div
      data-testid="annotation-layer-host"
      className="pointer-events-none fixed z-20"
      style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
    >
      <div className="relative h-full w-full" onClick={(e) => e.stopPropagation()}>
        <AnnotationLayer
          armed={armed}
          annotations={annotations}
          autoOpenLastId={autoOpenLastId}
          onAutoOpenConsumed={() => setAutoOpenLastId(null)}
          onAdd={(a) => { onChange([...annotations, a]); setAutoOpenLastId(a.id); }}
          onUpdate={(a) => { onChange(annotations.map((x) => (x.id === a.id ? a : x))); }}
          onRemove={(id) => { onChange(annotations.filter((x) => x.id !== id)); }}
        />
      </div>
    </div>
  );
}
